/**
 * Settings navigation configuration (structure only).
 * Resolve labels/headings with t() and paths with basePath in the consumer.
 */

export const SETTINGS_NAV_BASE = '/settings';

export type SettingsNavTarget = 'external' | 'internal';

export interface SettingsNavItemRaw {
  key: string;
  /** Path segment for internal (e.g. '/profile') or full URL for external */
  path: string;
  /** i18n key for label */
  labelKey: string;
  icon: string;
  target?: SettingsNavTarget;
}

export interface SettingsNavGroupRaw {
  /** i18n key for group heading */
  headingKey: string;
  items: SettingsNavItemRaw[];
}

export function resolveSettingsNavPath(item: SettingsNavItemRaw): string {
  if (item.target === 'external') {
    return item.path;
  }
  const segment = item.path.startsWith('/') ? item.path : `/${item.path}`;
  return `${SETTINGS_NAV_BASE}${segment}`;
}

export const SETTINGS_NAV_GROUPS_RAW: SettingsNavGroupRaw[] = [
  {
    headingKey: 'Account',
    items: [
      { key: 'profile', path: '/profile', labelKey: 'Profile', icon: 'solar:user-linear' },
    ],
  },
  {
    headingKey: 'Workspace',
    items: [
      { key: 'details', path: '/details', labelKey: 'Company Profile', icon: 'solar:document-text-linear' },
      { key: 'scheduler', path: '/scheduler', labelKey: 'Scheduler', icon: 'lucide:calendar' },
      { key: 'fga', path: '/FGA', labelKey: 'Access Management', icon: 'solar:shield-user-linear' },
      { key: 'work-queues', path: '/work-queues', labelKey: 'Work Queues', icon: 'solar:list-linear' },
      { key: 'watchlist', path: '/watchlist', labelKey: 'Watchlist', icon: 'solar:eye-linear' },
      { key: 'inspection-templates', path: '/inspection-templates', labelKey: 'Inspection Templates', icon: 'solar:checklist-minimalistic-linear' },
    ],
  },
  {
    headingKey: 'AI Configuration',
    items: [
      { key: 'behavior', path: '/behavior', labelKey: 'Behavior', icon: 'mdi:account-cog-outline' },
      { key: 'capabilities', path: '/capabilities', labelKey: 'Capabilities', icon: 'solar:widget-4-linear' },
      { key: 'phone-numbers', path: '/phone-numbers', labelKey: 'Phone Numbers', icon: 'solar:phone-linear' },
      { key: 'dictionary', path: '/dictionary', labelKey: 'Pronunciations', icon: 'solar:book-2-linear' },
    ],
  },
  {
    headingKey: 'Security',
    items: [
      { key: 'sso', path: '/sso', labelKey: 'Authentication', icon: 'solar:shield-keyhole-linear' },
    ],
  },
  {
    headingKey: 'Developer',
    items: [
      { key: 'apikey', path: '/apikey', labelKey: 'API Access Keys', icon: 'solar:code-linear' },
      { key: 'credentials', path: '/credentials', labelKey: 'Integration Credentials', icon: 'solar:plug-circle-linear' },
      { key: 'connectors', path: '/connectors', labelKey: 'Connectors', icon: 'solar:link-round-angle-linear' },
      { key: 'mcp-tools', path: '/mcp-tools', labelKey: 'MCP Tools', icon: 'solar:slider-vertical-linear' },
      { key: 'uxgenie', path: '/uxgenie', labelKey: 'UXGenie', icon: 'lucide:wand-sparkles' },
      // { key: 'formgenie', path: '/formgenie', labelKey: 'FormGenie', icon: 'mdi:form-select' },    -- Commented out for now, as FormGenie is not yet ready for release
      // { key: 'genie', path: '/genie', labelKey: 'Genie', icon: 'mdi:magic-staff' },
      { key: 'schema-mapper', path: '/schema-mapper', labelKey: 'Schema Mapper', icon: 'mynaui:git-merge' },
      { key: 'audit-logs', path: '/audit-logs', labelKey: 'Audit Logs', icon: 'lucide:chart-column' },
    ],
  },
];
